import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ChartMessage from 'components/ChartContainer/components/Chart/ChartMessage';
import { useInView } from 'react-cool-inview';
import {
    INITIAL,
    LOADING,
    SUCCESS,
    FAILED,
    UNAUTHORIZED,
    INVIEW_DELAY,
} from 'business/constants';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import NoData from 'assets/no_data.svg?react';
import DataLocked from 'assets/data_locked.svg?react';
import RequestError from 'assets/request_error.svg?react';
import _ from 'lodash';
import PermissionErrorText from 'ui/PermissionErrorText/PermissionErrorText';

export type ChartStatus =
    | 'initial'
    | 'success'
    | 'loading'
    | 'failed'
    | 'unauthorized';

type Props = {
    children: React.ReactNode;
    chartStatus: ChartStatus;
    disableLazyLoading: boolean;
    dataLength: number;
    breadcrumbs: React.ReactNode | null;
};

function StatusManager(props: Props): React.ReactElement {
    const { t } = useTranslation();
    const {
        children,
        chartStatus,
        dataLength,
        breadcrumbs,
        disableLazyLoading,
    } = props;
    const { observe, inView } = useInView({
        unobserveOnEnter: true,
        delay: INVIEW_DELAY,
    });
    const shouldDisplayChart = inView || disableLazyLoading;
    switch (chartStatus) {
        case INITIAL:
            return (
                <ChartMessage>
                    <SearchIcon />
                    <p>{t('chart.askQuery')}</p>
                </ChartMessage>
            );
        case LOADING:
            return (
                <>
                    {breadcrumbs}
                    <ChartMessage>
                        <CircularProgress
                            className="e2e-chart-loading"
                            data-testid="chart-loading"
                        />
                    </ChartMessage>
                </>
            );
        case SUCCESS: {
            if (dataLength === 0) {
                return (
                    <>
                        {breadcrumbs}
                        <ChartMessage>
                            <NoData />
                            <p>{t('common.noData')}</p>
                        </ChartMessage>
                    </>
                );
            }

            return (
                <Box
                    ref={observe}
                    sx={{
                        display: 'flex',
                        height: '100%',
                        flexDirection: 'column',
                    }}
                >
                    <ErrorBoundary>
                        {breadcrumbs}
                        {shouldDisplayChart && children}
                    </ErrorBoundary>
                </Box>
            );
        }
        case FAILED:
            return (
                <ChartMessage>
                    <RequestError />
                    <p className="e2e-request-error">
                        {t('common.requestError')}
                    </p>
                </ChartMessage>
            );
        case UNAUTHORIZED:
            return (
                <ChartMessage>
                    <DataLocked />
                    <PermissionErrorText />
                </ChartMessage>
            );
        default:
            return <></>;
    }
}

export default React.memo(StatusManager, _.isEqual);
